import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Badge, Chip, IconButton, Button,
    List, ListItem, ListItemAvatar, ListItemText,
    Avatar, Pagination, Tooltip, CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

import {
    fetchNotificationsFromAPI,
    markNotificationReadInAPI,
    markAllNotificationsReadInAPI,
} from '../../Redux/NotificacionesDucks';

dayjs.extend(relativeTime);
dayjs.locale('es');

// ── Constantes de tipo ─────────────────────────────────────────────────────────
const TYPE_CONFIG = {
    NewConversation: {
        label: 'Nueva conversación',
        icon: <ChatBubbleIcon fontSize="small" />,
        color: '#007AFF',
    },
    AgentRequested: {
        label: 'Agente requerido',
        icon: <PersonPinIcon fontSize="small" />,
        color: '#FF6B35',
    },
    UnansweredMessage: {
        label: 'Sin respuesta',
        icon: <AccessTimeIcon fontSize="small" />,
        color: '#F59E0B',
    },
    Window24hExpiring: {
        label: 'Ventana expirando',
        icon: <WarningIcon fontSize="small" />,
        color: '#EF4444',
    },
    NewQueueEntry: {
        label: 'Nueva en cola',
        icon: <HourglassEmptyIcon fontSize="small" />,
        color: '#7C3AED',
    },
    QueueReserved: {
        label: 'Reservado para ti',
        icon: <BookmarkIcon fontSize="small" />,
        color: '#F97316',
    },
};

const getTypeConfig = (type) =>
    TYPE_CONFIG[type] ?? { label: type, icon: <NotificationsIcon fontSize="small" />, color: '#6B7280' };

const isUrgentNotification = (notification) => {
    const type = String(notification?.notificationType || '').toLowerCase();
    return type === 'sin_respuesta' ||
        type === 'agente_requerido' ||
        type === 'unansweredmessage' ||
        type === 'agentrequested';
};

// ── Componente item ────────────────────────────────────────────────────────────
const NotificationItem = ({ notification, onRead }) => {
    const navigate = useNavigate();
    const config = getTypeConfig(notification.notificationType);

    const handleClick = () => {
        if (!notification.isRead) onRead(notification.id);
        if (notification.notificationType === 'QueueReserved' || notification.notificationType === 'NewQueueEntry') {
            navigate('/smart_talking/cola_espera');
        } else if (notification.conversationId) {
            navigate('/smart_talking/conversaciones', {
                state: { conversationId: notification.conversationId },
            });
        }
    };

    return (
        <ListItem
            alignItems="flex-start"
            onClick={handleClick}
            sx={{
                cursor: 'pointer',
                my: 0.9,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                px: 2, py: 1.5,
                borderRadius: 2,
                border: `1px solid ${notification.isRead ? '#dbe3ee' : alpha(config.color, 0.35)}`,
                borderLeft: `6px solid ${notification.isRead ? alpha(config.color, 0.65) : config.color}`,
                bgcolor: notification.isRead ? '#fff' : alpha(config.color, 0.06),
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                '&:hover': {
                    bgcolor: notification.isRead ? '#f8fbff' : alpha(config.color, 0.1),
                    borderColor: notification.isRead ? '#c7d4e3' : alpha(config.color, 0.5),
                    boxShadow: '0 2px 4px rgba(15, 23, 42, 0.08)',
                },
                transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
            }}
        >
            <ListItemAvatar>
                <Avatar sx={{ bgcolor: alpha(config.color, 0.15), color: config.color, width: 38, height: 38 }}>
                    {config.icon}
                </Avatar>
            </ListItemAvatar>

            <ListItemText
                primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={notification.isRead ? 400 : 600} sx={{ flex: 1 }}>
                        {notification.message}
                    </Typography>
                    {!notification.isRead && (
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: config.color, flexShrink: 0,
                            }} />
                    )}
                    <ChevronRightIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                </Box>
            }
                secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                            label={config.label}
                            size="small"
                            sx={{
                                bgcolor: alpha(config.color, 0.12),
                                color: config.color,
                                fontWeight: 500,
                                fontSize: 10,
                                height: 20,
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {dayjs(notification.createdAt).fromNow()}
                        </Typography>
                    </Box>
                }
            />
        </ListItem>
    );
};

// ── Página principal ───────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

const NotificacionesPage = () => {
    const dispatch = useDispatch();
    const { notifications, totalCount, unreadCount, loading } =
        useSelector((state) => state.notificaciones);

    const [page, setPage] = useState(1);
    const [filterKey, setFilterKey] = useState('todas');

    const load = useCallback(() => {
        dispatch(fetchNotificationsFromAPI(page, filterKey === 'sin_leer' ? true : null));
    }, [dispatch, page, filterKey]);

    // Carga inicial y polling cada 60s
    useEffect(() => {
        load();
        const interval = setInterval(load, 60_000);
        return () => clearInterval(interval);
    }, [load]);

    const handleMarkRead = (id) => dispatch(markNotificationReadInAPI(id));
    const handleMarkAll = () => dispatch(markAllNotificationsReadInAPI());

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const filteredNotifications = notifications.filter((notification) => {
        if (filterKey === 'sin_leer') return !notification.isRead;
        if (filterKey === 'urgente') return isUrgentNotification(notification);
        return true;
    });
    const filterOptions = [
        { key: 'todas', label: 'Todas', count: notifications.length },
        { key: 'sin_leer', label: 'Sin leer', count: notifications.filter(n => !n.isRead).length },
        { key: 'urgente', label: 'Urgente', count: notifications.filter(isUrgentNotification).length },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* ── Toolbar ─────────────────────────────────────────────────────── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5,
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#fff',
                flexShrink: 0,
            }}>
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsIcon sx={{ color: '#007AFF' }} />
                </Badge>

                <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                    Notificaciones
                </Typography>

                <Tooltip title="Marcar todas como leídas">
                    <span>
                        <IconButton
                            onClick={handleMarkAll}
                            disabled={unreadCount === 0}
                            size="small"
                            color="primary"
                        >
                            <DoneAllIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{
                display: 'flex', gap: 0.75, overflowX: 'auto',
                px: 2, py: 1, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                flexShrink: 0,
            }}>
                {filterOptions.map(filter => {
                    const active = filterKey === filter.key;
                    return (
                        <Button
                            key={filter.key}
                            onClick={() => { setFilterKey(filter.key); setPage(1); }}
                            sx={{
                                minHeight: 44,
                                borderRadius: 999,
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                bgcolor: active ? '#007AFF' : 'transparent',
                                color: active ? '#fff' : '#6b7280',
                                border: active ? '1px solid #007AFF' : '1px solid #d1d5db',
                                '&:hover': {
                                    bgcolor: active ? '#005ecb' : '#f9fafb',
                                    borderColor: active ? '#005ecb' : '#9ca3af',
                                },
                            }}
                        >
                            {`${filter.label} (${filter.count})`}
                        </Button>
                    );
                })}
            </Box>

            {/* ── Lista ───────────────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', bgcolor: '#f8fafc', px: 1.75, boxSizing: 'border-box' }}>
                {loading && notifications.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredNotifications.length === 0 ? (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '60%', gap: 2, color: 'text.secondary',
                    }}>
                        <NotificationsIcon sx={{ fontSize: 56, opacity: 0.3 }} />
                        <Typography variant="body2">
                            {filterKey === 'sin_leer'
                                ? 'No tienes notificaciones sin leer'
                                : filterKey === 'urgente'
                                    ? 'No hay notificaciones urgentes'
                                    : 'No hay notificaciones'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ py: 0.9 }}>
                        <List disablePadding>
                            {filteredNotifications.map((n) => (
                                <NotificationItem
                                    key={n.id}
                                    notification={n}
                                    onRead={handleMarkRead}
                                />
                            ))}
                        </List>
                    </Box>
                )}
            </Box>

            {/* ── Paginación ──────────────────────────────────────────────────── */}
            {totalPages > 1 && (
                <Box sx={{
                    display: 'flex', justifyContent: 'center',
                    py: 1.5, borderTop: '1px solid #e0e0e0',
                    bgcolor: '#fff', flexShrink: 0,
                }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, val) => setPage(val)}
                        size="small"
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
};

export default NotificacionesPage;
